
import { Component, Mixins, Prop } from "vue-property-decorator";
import { DocumentEditViewMixin } from ".";
import { 
  EmbeddedDocumentPostMessageResult,
  EmbeddedDocumentRegistrationPostMessageResult,
  EmbeddedDocumentStatePostMessage,
  EmbeddedDocumentWindowInformationPostMessageResult,
  EmbeddedDocumentObjectsPostMessageResult,
  EmbeddedDocumentPostMessageRequest,
} from "../../../domain/document";
import { IRestrictions } from "../editor";
import { DomainRegistry } from "../../../domain";

@Component
export default class EmbeddedDocumentEditViewMixin extends Mixins(DocumentEditViewMixin) {
  @Prop()
  shouldNotifyState: boolean;

  data(): any {
    return {
      embeddedComponent: null,
    };
  }
  embeddedComponent: any;

  mounted() {
    window.addEventListener("message", this.onParentWindowNotify, false);
    window.onbeforeunload = null;   // ページ遷移時に確認ダイアログを表示させない
  }

  onInitialized(): void {
    this.onDocumentInitialized();
    if (this.shouldNotifyState) {
      this.notifyMessage(
        new EmbeddedDocumentStatePostMessage(
          window.name,
          "notify",
          "ready",
        )
      );
    }
  }

  async onParentWindowNotify(event: any): Promise<void> {
    if (!event || (event && !event.data)) { return; }
    if (typeof event.data !== "string") { return; }
    const message = JSON.parse(event.data);
    this.$logger.debug("postMessage受信");
    console.log(message);

    switch (message.request) {
      case "register":
        this.registerEmbeddedDocument(message);
        break;
      case "cancel":
        this.cancelEditing(message);
        break;
      case "windowInformation":
        this.notifyWindowInfo(message);
        break;
      case "getDocumentObjects":
        this.getDocumentObjects(message);
        break;
    }
  }

  async registerEmbeddedDocument(requestMessage: EmbeddedDocumentPostMessageRequest): Promise<void> {
    if (!this.embeddedComponent) { return; }
    if (!this.isDocumentInitialized)  { 
      const action = requestMessage.isTemporary ? "一時保存" : "登録";
      this.$error(action + "失敗", "文書初期化中なので" + action + "できません。");
      this.notifyRegistrationFailure(requestMessage); 
      return; 
    }

    const registeredId = await this.embeddedComponent.registerDocument(requestMessage.isTemporary);
    if (!registeredId) {
      this.notifyRegistrationFailure(requestMessage);
    }
  }

  async cancelEditing(requestMessage: EmbeddedDocumentPostMessageRequest): Promise<void> {
    if (!this.embeddedComponent) { return; }
    try  {
      await this.embeddedComponent.cancelEditing();
      this.notifyMessage(
        new EmbeddedDocumentPostMessageResult(
          window.name,
          requestMessage.request,
          true,
        )
      );
      this.isEditing = false;
    } catch (ex) {
      this.notifyMessage(
        new EmbeddedDocumentPostMessageResult(
          window.name,
          requestMessage.request,
          false,
        )
      );
    }
  }
  
  async notifyRegistrationFailure(requestMessage: EmbeddedDocumentPostMessageRequest): Promise<void> {
    this.notifyMessage(
      new EmbeddedDocumentRegistrationPostMessageResult(
        window.name,
        requestMessage.request,
        false,
        {
          isTemporary: requestMessage.isTemporary,
          documentId: null,
          archiveId: null,
        },
      )
    );
  }

  async getDocumentObjects(requestMessage: EmbeddedDocumentPostMessageRequest): Promise<void> {
    if (!this.embeddedComponent) { return; }
    try {
      const repository = DomainRegistry.document.getDocumentRepository();
      const editingObjects = await repository.getEditingDocumentObjects(this.embeddedComponent.editingSource);
      this.notifyMessage(
        new EmbeddedDocumentObjectsPostMessageResult(
          window.name,
          requestMessage.request,
          true,
          editingObjects
        )
      );
    } catch (ex) {
      this.notifyMessage(
        new EmbeddedDocumentObjectsPostMessageResult(
          window.name,
          requestMessage.request,
          false,
          null,
        )
      );
    }
  }
  
  notifyWindowInfo(requestMessage: EmbeddedDocumentPostMessageRequest): void {
    if (!this.embeddedComponent || !this.embeddedComponent.isLoaded) {
      this.notifyMessage(
        new EmbeddedDocumentWindowInformationPostMessageResult(
          window.name,
          requestMessage.request,
          false,
          null,
        )
      );
      return;
    }
    const documentPage = document.getElementsByClassName("document-page-editor-page");
    const documentHeight = documentPage[0].clientHeight + 50; // コンボボックスが画面下の端にあると、クリックで出現する選択項目が見えないのでマージンで暫定対策している分の50pxを足す

    const header = document.getElementsByClassName("core-edit-menu-container header-pane-root");
    const headerHeight = header[0].clientHeight + 12; // EmbeddedEditMenuContainerのpadding分2px足す。フォーマットによってスクロールバーが出ることがあるので暫定対処で10px足す。

    const tab = document.getElementsByClassName("sub-tools");
    const navigator = document.getElementsByClassName("page-navigator");
    const width = documentPage[0].clientWidth + tab[0].clientWidth + navigator[0].clientWidth + 16; // EmbeddedEditMenuContainerのpadding分6px足す。フォーマットによってスクロールバーが出ることがあるので暫定対処で10px足す。

    const minHeight = documentHeight + headerHeight;
    const minWidth = Math.max(width, 685); // ヘッダーの幅が685より小さいとボタンが消えるので暫定対処
    this.notifyMessage(
      new EmbeddedDocumentWindowInformationPostMessageResult(
        window.name,
        requestMessage.request,
        true,
        {
          minheight: minHeight,
          minwidth: minWidth,
        }
      )
    );
  }

  notifyMessage(result: EmbeddedDocumentPostMessageResult): void {
    try {
      if (window.parent) {
        window.parent.postMessage(JSON.stringify(result), "*");
      }
    } catch (e) {
      console.log(e);
    }
  }

  async finishEmbeddedEditing(id: number, destinationRoute?: any, isTemporary?: boolean, archiveId?: number): Promise<void> {
    this.notifyMessage(
      new EmbeddedDocumentRegistrationPostMessageResult(
        window.name,
        "register",
        true,
        {
          isTemporary: isTemporary,
          documentId: id,
          archiveId: archiveId,
        },
      )
    );
    this.isEditing = false;
    this.notifyRegisteredMessage();
  }
  
  get restrictions(): IRestrictions {
    return {
      printing: {
        isDisabled: true,
      }
    };
  }
}
